import customAxios from "../serverCall";

/**
 * Common API utility functions to reduce code duplication
 */

// Standard HTTP methods
export const apiMethods = {
  GET: 'get',
  POST: 'post',
  PUT: 'put',
  PATCH: 'patch',
  DELETE: 'delete'
};
// 
/**
 * Generic API call function
 * @param {string} method - HTTP method
 * @param {string} endpoint - API endpoint
 * @param {Object} data - Request data
 * @param {Object} config - Additional axios config
 * @returns {Promise} API response
 */
export const makeApiCall = async (method, endpoint, data = null, config = {}) => {
  try {
    let response;
    if (method === apiMethods.GET || method === apiMethods.DELETE) {
      // GET and DELETE usually don't have a request body. 
      // If 'data' is provided but 'config.params' is not, use 'data' as params.
      const requestConfig = { ...config };
      if (data && !requestConfig.params) {
        requestConfig.params = data;
      }
      response = await customAxios[method](endpoint, requestConfig);
    } else {
      response = await customAxios[method](endpoint, data, config);
    }
    return response;
  } catch (error) {
    console.error(`API Error [${method.toUpperCase()} ${endpoint}]:`, error);
    throw error;
  }
};

/**
 * GET request helper
 */
export const apiGet = (endpoint, params = {}) => {
  return makeApiCall(apiMethods.GET, endpoint, null, { params });
};

/**
 * POST request helper
 */
export const apiPost = (endpoint, data = {}, config = {}) => {
  return makeApiCall(apiMethods.POST, endpoint, data, config);
};

/**
 * PUT request helper
 */
export const apiPut = (endpoint, data = {}) => {
  return makeApiCall(apiMethods.PUT, endpoint, data);
};

/**
 * PATCH request helper
 */
export const apiPatch = (endpoint, data = {}) => {
  return makeApiCall(apiMethods.PATCH, endpoint, data);
};

/**
 * DELETE request helper
 */
export const apiDelete = (endpoint, data = {}) => {
  return makeApiCall(apiMethods.DELETE, endpoint, data);
};

/**
 * File upload helper
 * Do not set Content-Type header - axios will automatically set
 * multipart/form-data with the correct boundary when FormData is used.
 * Setting it manually prevents axios from adding the boundary parameter.
 * Axios automatically handles full URLs (https://...) and won't prepend baseURL.
 */
export const apiUpload = (endpoint, formData) => {
  // Log for debugging (remove in production if needed)
  console.log('Uploading to:', endpoint);
  console.log('FormData entries:', Array.from(formData.entries()).map(([key, value]) => ({
    key,
    value: value instanceof File ? { name: value.name, type: value.type, size: value.size } : value
  })));
  
  return makeApiCall(apiMethods.POST, endpoint, formData);
};

/**
 * Standard pagination parameters builder
 */
export const buildPaginationParams = ({ page = 1, pageSize = 10, searchtext = "" }) => ({
  page,
  limit: pageSize,
  searchtext,
});

/**
 * Standard pagination body builder
 */
export const buildPaginationBody = ({ page = 1, pageSize = 10, searchtext = "" }) => ({
  pagination: {
    page,
    pageSize,
  },
  searchtext,
});

/**
 * API endpoint constants
 */
export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: '/api/v1/admin/auth/login',
    UPDATE_PROFILE: '/api/v2/user/updateprofile',
  },

  // Comments
  COMMENTS: {
    GET_ALL: '/api/v1/admin/getComments',
    GET_DETAILS: '/api/v1/admin/getCommentDetails',
    UPDATE: '/api/v1/admin/updateComments',
  },

  // Events
  EVENTS: {
    GET_ALL: '/api/v1/admin/events-posts',
    GET_DETAILS: '/api/v1/admin/getMemberEventDetails',
    CREATE: '/api/v2/post-event/create',
    UPDATE: '/api/v1/post-event/update',
    DELETE: '/api/v1/admin/delete/events-posts',
    UPDATE_LIKE_COUNT: '/api/v1/admin/posts-events/update-likecount',
  },

  // Feed Posts
  FEED_POSTS: {
    GET_ALL: '/api/v1/admin/feed-posts',
    DELETE: '/api/v1/admin/delete/feed-posts',
  },

  // Categories
  CATEGORIES: {
    GET_ALL: '/api/v1/admin/category',
    CREATE: '/api/v1/admin/category/create',
    UPDATE: '/api/v1/admin/category/update',
    DELETE: '/api/v1/admin/category/delete',
  },

  // Event Types (Car Types)
  EVENT_TYPES: {
    GET_ALL: '/api/v1/admin/car-types',
    CREATE: '/api/v1/admin/car-types/create',
    UPDATE: '/api/v1/admin/car-types/update',
    DELETE: '/api/v1/admin/car-types/delete',
  },

  // Post Types
  POST_TYPES: {
    GET_ALL: '/api/v1/admin/post-types',
    CREATE: '/api/v1/admin/post-types/create',
    UPDATE: '/api/v1/admin/post-types/update',
    DELETE: '/api/v1/admin/post-types/delete',
  },

  // Vendor Categories
  PRODUCT_CATEGORIES: {
    GET_ALL: '/api/v1/admin/product-categories',
    CREATE: '/api/v1/admin/product-category/create',
    CREATE_SUB: '/api/v1/admin/subcategory/create',
    UPDATE_ORDER: '/api/v1/admin/product-categories/update-order',
    UPDATE: '/api/v1/admin/product-category',
    UPDATE_SUB: '/api/v1/admin/subcategory',
  },

  // Users
  USERS: {
    GET_ALL: '/api/v1/admin/users',
    GET_DETAILS: '/api/v1/admin/getUserDetails',
    UPDATE: '/api/v1/admin/user/activation',
    UPGRADE: '/api/v1/admin/user/upgrade',
    DELETE: '/api/v1/admin/user/delete',
  },

  // Accounts
  ACCOUNTS: {
    GET_ALL: '/api/v1/admin/accounts',
    UPDATE: '/api/v1/admin/account/activation',
  },

  // Organizations
  ORGANIZATIONS: {
    GET_REQUESTS: '/api/v1/admin/getOrganization',
    UPDATE_REQUESTS: '/api/v1/admin/updateOrganization',
    GET_EVENTS: '/api/v1/admin/org/getorgreq',
    UPDATE_EVENTS: '/api/v1/admin/org/updateorgreq',
    GET_DETAILS: '/api/v1/admin/getOrganizationDetails',
  },

  // Garages
  GARAGES: {
    GET_ALL: '/api/v1/admin/garages',
    DELETE: '/api/v1/admin/delete/garages',
  },

  // Vendors
  VENDORS: {
    GET_REQUESTS: '/api/v1/admin/vendor-requests',
    APPROVE: (id) => `/api/v1/admin/vendor-request/${id}/approve`,
    REJECT: (id) => `/api/v1/admin/vendor-request/${id}/reject`,
    ADD: '/api/v1/admin/vendor/add',
    EDIT: '/api/v1/admin/vendor/edit',
  },

  // Vendor Change Requests
  VENDOR_CHANGE_REQUESTS: {
    GET_ALL: '/api/v1/admin/vendor-change-requests',
    RESPOND: (id) => `/api/v1/admin/vendor-change-request/${id}/respond`,
  },

  // Reports
  REPORTS: {
    GET_ALL: '/api/v1/admin/reports',
  },

  // Contacts
  CONTACTS: {
    GET_ALL: '/api/v1/admin/contactus',
    DELETE: '/api/v1/admin/contactus/delete',
  },

  // Prices
  PRICES: {
    GET_ALL: '/api/v1/admin/products/prices',
    UPDATE: '/api/v1/admin/products/prices',
  },

  // Vendor Boost Prices
  VENDOR_BOOST_PRICES: {
    GET_ALL: '/api/v1/admin/vendor-promotions/price',
    UPDATE: '/api/v1/admin/vendor-promotions/price',
  },

  // Notifications
  NOTIFICATIONS: {
    SEND: '/api/v2/notify-topic',
  },

  // Settings
  SETTINGS: {
    BASE: '/api/v1/admin/settings',
  },

  // Manual messages
  MANUAL_MESSAGES: {
    SEND: '/api/v1/admin/send-manual-message',
  },

  // File Upload
  UPLOAD: 'https://devapi.car-hangout.com/api/v2/upload',

  // Custom Products
  CUSTOM_PRODUCTS: {
    GET_ALL: '/api/v1/admin/custom-products',
    ADD: '/api/v1/admin/custom-products/add',
    UPDATE: '/api/v1/admin/custom-products/edit',
    DELETE: '/api/v1/admin/custom-products/delete',
    GET_DETAILS: (id) => `/api/v1/admin/custom-products/${id}`,
  },

  // Dashboard
  DASHBOARD: {
    GET_COUNTS: '/api/v1/admin/dashboard/counts',
    GET_COMMISSION: '/api/v1/admin/dashboard/commission',
  },

  // Gamification
  GAMIFICATION: {
    BADGES: '/api/v1/admin/gamification/badges',
    UPDATE_BADGE: (id) => `/api/v1/admin/gamification/badge/${id}`,
    LEVELS_REORDER: '/api/v1/admin/gamification/levels/reorder',
    ACHIEVEMENT_BADGES: '/api/v1/admin/gamification/achievement-badges',
    UPDATE_ACHIEVEMENT_BADGE: (id) => `/api/v1/admin/gamification/achievement-badge/${id}`,
    ACHIEVEMENT_BADGES_REORDER: '/api/v1/admin/gamification/achievement-badges/reorder',
    FEATURE_UNLOCKS: '/api/v1/admin/gamification/feature-unlocks',
    UPDATE_FEATURE_UNLOCK: (id) => `/api/v1/admin/gamification/feature-unlock/${id}`,
    FEATURE_UNLOCKS_REORDER: '/api/v1/admin/gamification/feature-unlocks/reorder',
    DAILY_TASKS: '/api/v1/admin/gamification/daily-tasks',
    TODAY_USER_TASKS: '/api/v1/admin/gamification/daily-tasks/today-user-tasks',
    DAILY_TASKS_SCHEDULE: '/api/v1/admin/gamification/daily-tasks/schedule',
    UPDATE_DAILY_TASK: (id) => `/api/v1/admin/gamification/daily-task/${id}`,
    DAILY_TASKS_UPLOAD: '/api/v1/admin/gamification/daily-tasks/upload',
  },
};
